import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { getUser, logUserOut } from "../utils/helper";
import { useTheme } from "../contexts/ThemeContext";
import { useSettings } from "../contexts/SettingsContext";

// New-UI application shell: sidebar + topbar + content. Wraps protected pages.
// Structure/typography match the Startise Attendance design (Startise Attendance.dc.html).
const ROUTE_TITLES = {
  "/dashboard": "Dashboard",
  "/members": "Members",
  "/history": "Attendance history",
  "/rewards": "Reward contest",
  "/roles": "Roles & permissions",
  "/settings": "Settings",
};
const ROUTE_SUBS = {
  "/members": "Team directory across the workspace",
  "/history": "Browse and export past records",
  "/rewards": "Monthly eligibility for the reward contest",
  "/roles": "Manage who can administer attendance",
  "/settings": "Workspace, hours and integrations",
};

// Startise 4-colour brand mark.
function BrandLogo({ size = 34 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" aria-hidden="true">
      <path d="M233.237 355.464C233.237 408.279 190.422 451.072 137.63 451.072C84.8153 451.072 42 408.257 42 355.464H233.237Z" fill="#FDB849" />
      <path d="M177.079 61C102.504 61 42.0476 121.457 42.0476 196.031C42.0476 223.297 50.1305 248.692 64.0499 269.901C69.1596 277.698 75.0621 284.944 81.6033 291.485C106.028 315.91 139.792 330.997 177.079 330.997L272.576 61H177.079Z" fill="#09A7DF" />
      <path d="M470.727 156.608C470.727 103.793 427.912 61 375.12 61C322.305 61 279.512 103.815 279.512 156.608H470.749H470.727Z" fill="#665DA9" />
      <path d="M335.652 451.074C410.227 451.074 470.684 390.617 470.684 316.043C470.684 288.776 462.601 263.382 448.681 242.173C443.572 234.376 437.669 227.13 431.128 220.589C406.703 196.164 372.94 181.077 335.652 181.077L240.155 451.096H335.652V451.074Z" fill="#F0679F" />
    </svg>
  );
}

export default function AppLayout({ children }) {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { settings } = useSettings();
  const user = getUser() || {};
  // Below 1024px the sidebar leaves the grid and slides in as an overlay drawer.
  const [navOpen, setNavOpen] = useState(false);

  // Close on navigation — without this the drawer stays open over the page just loaded.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  // Escape closes it, and the page behind must not scroll while it's open.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setNavOpen(false); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [navOpen]);

  const orgName = settings?.organisation_name || "Startise";
  const timezone = settings?.timezone || "Asia/Dhaka";

  let title = ROUTE_TITLES[location.pathname];
  if (!title) title = location.pathname.startsWith("/members/") ? "Member profile" : "Dashboard";

  // Today's date in the workspace's configured timezone (not the browser's).
  const dateStr = new Intl.DateTimeFormat("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: timezone,
  }).format(new Date());
  let sub = ROUTE_SUBS[location.pathname];
  if (location.pathname === "/dashboard") sub = `${dateStr} — today's snapshot`;
  if (!sub) sub = location.pathname.startsWith("/members/") ? "Attendance and activity detail" : dateStr;

  const initials = (user.name || "Admin")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="app">
      {/* Dims and captures taps behind the drawer; only exists while it's open. */}
      {navOpen && <div className="nav-backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />}

      {/* Sidebar */}
      <aside className={`sidebar${navOpen ? " open" : ""}`}>
        <div className="brand">
          <div className="brand-mark"><BrandLogo /></div>
          <div>
            <div className="brand-name">{orgName}</div>
            <div className="brand-sub">Attendance</div>
          </div>
        </div>

        <nav className="nav">
          <div className="nav-sect">Main</div>
          <NavLink className="nav-item" to="/dashboard"><svg className="ni"><use href="#i-home" /></svg>Dashboard</NavLink>
          <NavLink className="nav-item" to="/members"><svg className="ni"><use href="#i-users" /></svg>Members</NavLink>
          <NavLink className="nav-item" to="/history"><svg className="ni"><use href="#i-history" /></svg>History</NavLink>
          <NavLink className="nav-item" to="/rewards"><svg className="ni"><use href="#i-trend" /></svg>Rewards</NavLink>

          <div className="nav-sect mt">Administration</div>
          <NavLink className="nav-item" to="/roles"><svg className="ni"><use href="#i-lock" /></svg>Roles &amp; access</NavLink>
          <NavLink className="nav-item" to="/settings"><svg className="ni"><use href="#i-settings" /></svg>Settings</NavLink>
        </nav>

        <div className="sidebar-foot">
          <div className="admin-row">
            {user.imgUrl ? (
              <img className="av av-36" src={user.imgUrl} alt="" style={{ objectFit: "cover" }} />
            ) : (
              <div className="av av-36 av-MH">{initials}</div>
            )}
            <div className="admin-info">
              <b>{user.name || "Dashboard Admin"}</b>
              <span>{user.title || "Administrator"}</span>
            </div>
          </div>
          <button type="button" className="nav-item nav-item-btn" onClick={() => logUserOut()}>
            <svg className="ni"><use href="#i-logout" /></svg>Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="main">
        <header className="topbar">
          <button
            type="button"
            className="nav-toggle"
            onClick={() => setNavOpen((v) => !v)}
            aria-label={navOpen ? "Close menu" : "Open menu"}
            aria-expanded={navOpen}
          >
            <svg><use href={`#i-${navOpen ? "close" : "menu"}`} /></svg>
          </button>
          <div className="topbar-left">
            <div className="topbar-title">{title}</div>
            <div className="topbar-sub">{sub}</div>
          </div>
          <div className="topbar-right">
            <div className="live-pill"><span className="dot" />Live · {timezone}</div>
            <button type="button" className="theme-toggle" onClick={toggleTheme} aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
              <svg><use href={`#i-${theme === "dark" ? "sun" : "moon"}`} /></svg>
            </button>
            <button type="button" className="icon-btn" aria-label="Notifications">
              <svg><use href="#i-bell" /></svg>
              <span className="notif-dot" aria-hidden="true"></span>
            </button>
          </div>
        </header>

        <div className="content">{children}</div>
      </div>
    </div>
  );
}
